import { MultiAutoCompleteItem } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { CompanyEnrichAutoCompleteItem } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import { EMPLOYEE_RANGES } from "@/pages/Private/pages/CompanyEnrich/constants/employees.constants";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";

import { CreatedOnboardingLists, readCreatedLists } from "../lists/onboardingCreatedLists";
import { LeadDbConfig, LookalikeConfig } from "../lists/onboardingListTypes";

import { EMPTY_TRACK_FILTERS, TrackFilterValues } from "./trackFilters";

/** The Lists-step configs the Track search can be seeded from, as-is. */
export type TrackSeedConfig = LeadDbConfig | LookalikeConfig;

/**
 * The criteria the Track step starts from when the previous step already built a
 * people search: the lead database first — it is the richest of the two and its
 * filters describe people directly — and the lookalike list otherwise.
 */
export const trackSeedConfigOf = (created: CreatedOnboardingLists): TrackSeedConfig | null => {
	const leadDb = created.leadDb?.config;

	if (leadDb?.kind === "leadDb") {
		return leadDb;
	}

	const lookalike = created.lookalike?.config;

	return lookalike?.kind === "lookalike" ? lookalike : null;
};

/** A fixed-option filter's stored ids, re-labelled from the option list they came from. */
const optionItems = (values: string[], options: MultiAutoCompleteItem[]): MultiAutoCompleteItem[] =>
	values.map((value) => ({
		id: value,
		title: options.find((option) => String(option.id) === value)?.title ?? value,
	}));

const countryItems = (
	codes: string[],
	titleOf: (code: string) => string
): CompanyEnrichAutoCompleteItem[] => codes.map((code) => ({ id: code, title: titleOf(code) }));

/** Job titles are stored as free text, so the title doubles as the item id. */
const positionItems = (titles: string[]): CompanyEnrichAutoCompleteItem[] =>
	titles.map((title) => ({ id: title, title }));

/**
 * A picked list's criteria as the Track step's filter values.
 *
 * Two of them are deliberately dropped, both for the same reason — a tracker
 * list watches accounts for signals, a scope the criteria of a one-off lead
 * list are far too tight for:
 *
 * - the lookalike's **seed domains**, which narrow the search to companies
 *   resembling a handful of named ones;
 * - the lead database's **company keywords**, which require the words the chat
 *   picked ("NIS2 compliance software") to appear on the company itself.
 *
 * Everything else they describe (size, HQ, industries, the people to find)
 * still applies, and the user can always add keywords back in the rail.
 */
export const trackFiltersFromConfig = (
	config: TrackSeedConfig,
	titleOf: (code: string) => string
): TrackFilterValues => ({
	...EMPTY_TRACK_FILTERS,
	positionQuery: positionItems(config.positionQuery),
	seniority: optionItems(config.seniority, SENIORITY_OPTIONS),
	department: config.kind === "leadDb" ? optionItems(config.department, DEPARTMENT_OPTIONS) : [],
	countries: countryItems(config.countries, titleOf),
	industries: config.kind === "leadDb" ? config.industries : [],
	employees: optionItems(config.employees, EMPLOYEE_RANGES),
	companyCountries: countryItems(config.companyCountries, titleOf),
	semanticQuery: config.semanticQuery,
});

/**
 * Resolves the country codes the config stores to the names the filter rail
 * shows. One lookup for the whole list, and only when the config has countries
 * at all; a failed lookup falls back to the code so the search still runs on it.
 */
const countryTitleResolver = async (
	codes: string[],
	fetchAllCountries: () => Promise<CompanyEnrichAutoCompleteItem[]>
): Promise<(code: string) => string> => {
	if (codes.length === 0) {
		return (code) => code;
	}

	try {
		const countries = await fetchAllCountries();
		const titles = new Map(countries.map((country) => [country.id, country.title]));

		return (code) => titles.get(code) ?? code;
	} catch (err) {
		console.error(err);

		return (code) => code;
	}
};

/**
 * The Track step's starting filters taken straight from the list the user just
 * built, or `null` when they built neither a lead database nor a lookalike list
 * — in which case the step falls back to deriving them from the ICP.
 */
export const seedTrackFilters = async (
	userId: number,
	fetchAllCountries: () => Promise<CompanyEnrichAutoCompleteItem[]>
): Promise<TrackFilterValues | null> => {
	const config = trackSeedConfigOf(readCreatedLists(userId));

	if (!config) {
		return null;
	}

	const titleOf = await countryTitleResolver(
		[...config.countries, ...config.companyCountries],
		fetchAllCountries
	);

	return trackFiltersFromConfig(config, titleOf);
};
